<template>
	<Draggable
		class="block-editor-widget-layout-draggable col text-left"
		:tag="{ value: 'div', props: { class: fieldCols(grid) } }"
		:class="{ 'is-selected': isCurrentEdited, 'is-mini-app': isMiniApp }"
	>
		<div class="wrpr" @click="updateEditField(field)">
			<div class="selection-indication" v-if="!isMiniApp"></div>

			<div class="drag_sign">
				<SIcon name="drag-sign" />
			</div>

			<div v-if="isMiniApp && fieldConf.widgetEditorComponent" class="non-drag-area-class">
				<component :is="fieldConf.widgetEditorComponent"></component>
			</div>
			<BlockEditorWidgetLayoutRowEdit
				v-else
				:field="field"
				v-on="$listeners"
				:editMode="editMode"
				ref="field_container"
				class="non-drag-area-class"
				:testResponse="testResponse"
				:previousSteps="previousSteps"
				:replaceInsertDataStringDict="replaceInsertDataStringDict"
				:configurationsMappingBlocksUUIDs="configurationsMappingBlocksUUIDs"
			/>
			<div v-if="!isMiniApp" class="field-action-buttons display-flex flex-column flex-end flex-justify-space-between">
				<AppTooltip :text="'Delete'" :position="'right'">
					<v-btn
						icon
						x-small
						rounded
						width="20"
						height="20"
						class="field-action-button"
						@click.stop="deleteWidgetField({ dontDeleteTestResponse: true })"
					>
						<SIcon :name="'trash'" class="si" />
					</v-btn>
				</AppTooltip>
				<AppTooltip :text="'Duplicate'" :position="'right'">
					<v-btn icon x-small rounded width="20" height="20" @click="duplicateField" class="field-action-button">
						<SIcon :name="'duplicate'" class="si" />
					</v-btn>
				</AppTooltip>
				<MoveConfigurationDropdown :isGeneralConfiguration="isGeneralConfiguration" v-if="showMoveConfigurationOption" />
			</div>
		</div>
	</Draggable>
</template>

<script>
/* eslint-disable no-unreachable */
import MoveConfigurationDropdown from '@/views/Flow/configurations/SetupMenu/CredentialsSelector/MoveConfigurationDropdown';
import { Draggable } from 'vue-smooth-dnd';
import { mapActions, mapGetters } from 'vuex';
import AppTooltip from '@/components/common/AppTooltip';
import BlockEditorWidgetLayoutRowEdit from './BlockEditorWidgetLayoutRowEdit';
import widgetFieldTypes from '../BlockEditorWidget/widgetFieldTypes';
import { NONE_INTERFACE_CONFIGURATION_ONLY_FIELDS } from '@/components/BlockEditorWidget/widgetFieldTypes.const';

export default {
	name: 'BlockEditorWidgetLayoutDraggable',
	components: {
		Draggable,
		AppTooltip,
		MoveConfigurationDropdown,
		BlockEditorWidgetLayoutRowEdit,
	},
	props: {
		field: { type: Object },
		editMode: { type: Boolean },
		testResponse: { type: Object },
		previousSteps: { type: Object },
		replaceInsertDataStringDict: { type: Object },
		configurationsMappingBlocksUUIDs: { type: Array },
		relativeToField: { type: Object },
		isGeneralConfiguration: { type: Boolean },
	},
	computed: {
		...mapGetters('selectedNode', ['isMiniApp']),
		...mapGetters('widgetEditor', ['widgetFields', 'editField']),
		showMoveConfigurationOption() {
			return this.editMode && !NONE_INTERFACE_CONFIGURATION_ONLY_FIELDS.includes(this.field.field_type);
		},
		grid() {
			if (this.field.metadata.options.layout) {
				return this.field.metadata.options.layout.grid;
			}
			return 12;
		},
		fieldTitle() {
			const title = this.field.metadata.options.title;
			let tempName = '';
			if (title.value) {
				if (!title.enabled || !title.value) {
					tempName = 'New Field (' + this.field.id + ')';
				} else {
					tempName = title.value;
				}
			}
			return tempName;
		},
		addFieldsButtons() {
			return [
				{ name: 'tc', enabled: true },
				{ name: 'bc', enabled: true },
			];
		},
		isCurrentEdited() {
			return this.editField && this.editField.id === this.field.id;
		},
		fieldConf() {
			return widgetFieldTypes[this.field.field_type];
		},
	},
	methods: {
		...mapActions('widgetEditor', ['addField', 'setEditField', 'deleteWidgetField', 'duplicateField']),
		fieldCols(grid) {
			let g = 'col-12';
			if (grid !== 'fill') {
				g = 'col-' + grid;
			}
			return g;
		},
		showMenu(btnDir, e) {
			this.$emit('showMenu', this.field, e, btnDir.name, this.$refs.field_container.getBoundingClientRect());
		},
		updateEditField(field) {
			this.setEditField(field);
		},
	},
};
</script>

<style scoped lang="scss">
.v-tooltip__content {
	opacity: 1 !important;
	padding: 6px 5px !important;
	color: var(--bg-color) !important;
	font: normal normal normal 12px proxima-nova !important;
	background-color: var(--tooltop-black-background) !important;
}
.v-tooltip__content:after {
	width: 0 !important;
	top: 10px !important;
	height: 0 !important;
	content: '' !important;
	left: -7px !important;
	position: absolute !important;
	transform: rotate(90deg) !important;
	border-left: 5px solid transparent !important;
	border-right: 5px solid transparent !important;
	border-top: 5px solid var(--tooltop-black-background) !important;
}

.block-editor-widget-layout-draggable {
	$widget-field-z-index: 10;
	$selected-widget-field-z-index: 11;
	position: relative;
	z-index: $widget-field-z-index;
	&.smooth-dnd-draggable-wrapper {
		overflow: visible;
	}

	&:hover {
		.row_controllers .add-field-btn {
			opacity: 1;
			visibility: visible;
		}
	}
	&:hover,
	&.is-selected {
		z-index: $selected-widget-field-z-index;
		.drag_sign {
			opacity: 1;
			visibility: visible;
		}
	}
	&.is-selected {
		.selection-indication {
			border: 1px solid var(--primary-color);
			border-radius: 6px;
		}
		.field-action-buttons {
			opacity: 1;
			visibility: visible;
		}
	}

	.wrpr {
		position: relative;
		margin: 3px 20px 5px 20px;
	}

	.selection-indication,
	.row_controllers {
		position: absolute;
		left: 5px;
		top: 7px;
		bottom: 0;
		right: 5px;
		pointer-events: none;
		border-radius: 6px;
		transition: 0.4s;
	}

	.selection-indication {
		z-index: 0;
	}

	.row_controllers {
		z-index: 2; // changed 2 --> 0 so RichTextEMenu is not hidden
		cursor: pointer;

		&:hover .add-field-btn {
			opacity: 1;
			visibility: visible;
			transition: 0.4s;
		}
		.add-field-btn {
			position: absolute;
			top: 0;
			left: 0;
			margin-top: -1px;
			opacity: 0;
			z-index: 2;
			visibility: hidden;
			pointer-events: all;

			&:hover {
				cursor: pointer !important;

				.inline-block,
				svg {
					cursor: pointer !important;
				}
			}
			&.tc {
				top: 0;
				left: 50%;
				transform: translate(-50%, -50%);
			}
			&.rc {
				top: 50%;
				right: 0%;
				left: auto;
				transform: translate(50%, -50%);
			}
			&.bc {
				top: 100%;
				left: 50%;
				transform: translate(-50%, -50%);
				margin-top: -3px !important;
			}
		}

		.add-field-btn-content {
			position: relative;
			cursor: pointer;
			width: 19px;
			height: 19px;
			border-radius: 50%;
			box-shadow: 0px 2px 3px rgba(0, 0, 0, 0.2);
			transform: scale(0.9);
			transform-origin: center center;
		}
	}

	&.is-mini-app {
		.l_row {
			border: 2px solid transparent;
		}

		::v-deep {
			.theme--light.v-card,
			.theme--light.v-tabs-items {
				background-color: transparent !important;
			}
			.drag_sign {
				display: none;
			}
		}
		&:hover,
		&.is-selected {
			.l_row {
				// cursor: inherit !important;
				border: 2px solid var(--header-background);
				border-radius: 9px;
			}
		}
	}
}

.drag_sign {
	position: absolute;
	left: 0px;
	top: 50%;
	transform: translate(-150%, -50%);
	opacity: 0;
	visibility: hidden;
	transition: opacity 0.5s;
	cursor: grab;
}
.field-action-buttons {
	top: 50%;
	opacity: 0;
	right: -18px;
	position: absolute;
	visibility: hidden;
	transition: opacity 0.5s;
	transform: translateY(-50%);

	.field-action-button {
		padding-top: 2px;
		background-color: #f0f1f3;
	}
}
</style>
